import { AnalyticsService } from 'base/analytics';
import { exists } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import { Button, ButtonPalette, RadioButtonGroup } from 'base/ui/button';
import { formatError } from 'base/ui/form';
import { IconAddComponent, IconCopyAi, IconShuffleAi } from 'base/ui/icons';
import { Rows } from 'base/ui/layout';
import { menuItem } from 'base/ui/menu_model';
import { DividerLine } from 'base/ui/panel';
import { SelectButton } from 'base/ui/select';
import { PromptTextarea, Textarea } from 'base/ui/textarea';
import { TextSmall, TextSmallLight, TextTinyLight } from 'base/ui/typography';
import {
  componentHasMenus,
  countComponentTabs,
} from 'blocks/component_renderer/decorators';
import { isNavbarWithDropdownsOnly } from 'blocks/component_renderer/navbars';
import { FeatureFlagsStore } from 'blocks/flag_overrides';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { isUserWrittenName } from '../ai/page_sections';
import {
  LimitCounter,
  SECTION_DESCRIPTION_LIMIT,
  SECTION_NAME_LIMIT,
} from '../ai/validation';
import { SectionEditingActions } from '../editing_actions/section';
import { EditorMode, editorModeStore } from '../editor_mode';
import { ElementInstance, Section, SectionRef } from '../model';
import { SelectionState } from '../selection/states';
import { ProjectSession } from '../session/session';
import { CommonPanelProps } from '../sidebar/panel_props';
import { SidebarNavigation } from '../sidebar/routes';
import { globalElementViewOptions } from '../wireframe/element_view_options';
import { GlobalSectionsButton } from './global_sections';
import { SectionDesignControls } from './section_design';
import {
  ComponentPreviewLayout,
  ComponentPreviewLoading,
  RequiredStar,
} from './ui';
import { LabelledOption, VariantToggles } from './variants';

export const WireframeEditSection = observer(function _WireframeEditSection({
  sel,
  section,
  sectionEditing,
  navigation,
  selection,
  assistant,
  session,
  editing: projectEditing,
  selectionActions,
  library,
  analytics,
}: {
  sel: SelectionState & { section: SectionRef };
  section: Section;
  sectionEditing: SectionEditingActions;
} & CommonPanelProps) {
  return (
    <Rows spacing={16}>
      {section.element.current && (
        <>
          <GlobalSectionsButton
            sref={sel.section}
            sectionEditing={sectionEditing}
          />
          <SectionTitleDescription
            section={section}
            sectionEditing={sectionEditing}
          />
          <DividerLine />
          <ComponentPreview
            component={section.element.current}
            sectionEditing={sectionEditing}
            section={section}
            navigation={navigation}
            session={session}
            analytics={analytics}
            library={library}
          />
          <VariantToggles
            page={sel.page}
            sref={sel.section}
            section={section}
            library={library}
            session={session}
            assistant={assistant}
            selection={selection}
            analytics={analytics}
          />
          <ViewOptions
            element={section.element.current}
            library={library}
            flags={session.flags}
          />
        </>
      )}

      <DividerLine />

      <Rows spacing={8}>
        {section.element.current &&
          editorModeStore.get() === EditorMode.Design && (
            <SectionDesignControls
              project={session.project}
              session={session}
              library={library}
              element={section.element.current}
              navigation={navigation}
              selection={selection}
            />
          )}
        {section.element.current == null && (
          <Button
            variant="secondary"
            iconColor="ai"
            icon={IconAddComponent}
            label="Add component"
            onClick={() => projectEditing.shuffleSectionComponent(sel.section)}
            disabled={!isUserWrittenName(section.name)}
            tooltip={
              !isUserWrittenName(section.name)
                ? 'Please write a section name first'
                : undefined
            }
          />
        )}
        {section.element.current && (
          <Button
            variant="secondary"
            iconColor="ai"
            icon={IconShuffleAi}
            label="Shuffle component"
            onClick={() => projectEditing.shuffleSectionComponent(sel.section)}
          />
        )}
        {section.element.current && (
          <Button
            label="Generate copy"
            icon={IconCopyAi}
            onClick={() => {
              session.analytics.track({
                event: 'project:copy_generate',
                copy_generate_scope: 'section',
                has_copy: sectionEditing.resetCopy != null,
                component_slug: section.element.current?.source.slug,
              });
              assistant.generateCopy(new Map([[sel.section, undefined]]));
            }}
            variant="secondary"
            iconColor="ai"
          />
        )}
      </Rows>
    </Rows>
  );
});

export const SectionTitleDescription = observer(function _SectionTitleDesc({
  section,
  sectionEditing,
}: {
  section: Section;
  sectionEditing: SectionEditingActions;
}) {
  // The panel is kind of far from the component, so we want to trigger the
  // warnings as if the component was editing when it is hidden:
  const editing = sectionEditing.whenComponentHidden;
  return (
    <>
      <Rows spacing={8}>
        <TextTinyLight color="grey">
          Name
          <RequiredStar />
        </TextTinyLight>
        <TextSmallLight tag="div">
          <Textarea
            value={section.name}
            onChange={editing.setName}
            placeholder="Section title"
          />
        </TextSmallLight>
        <LimitCounter value={section.name} limit={SECTION_NAME_LIMIT} />
      </Rows>

      <Rows spacing={8}>
        <TextTinyLight color="grey">Description</TextTinyLight>
        <TextSmallLight tag="div">
          <PromptTextarea
            value={section.description}
            onChange={editing.setDescription}
            placeholder="Describe this section"
            allowNewlines
          />
        </TextSmallLight>
        <LimitCounter
          value={section.description}
          limit={SECTION_DESCRIPTION_LIMIT}
        />
      </Rows>
    </>
  );
});

const ComponentPreview = observer(function _ComponentPreview({
  component,
  sectionEditing,
  analytics,
  library,
  section,
  navigation,
  session,
}: {
  component: ElementInstance;
  sectionEditing: SectionEditingActions;
  analytics: AnalyticsService;
  library: LibraryCache;
  section: Section;
  navigation: SidebarNavigation;
  session: ProjectSession;
}) {
  const state = library.getCachedPublicComponent(component.source.slug, {
    fetchIfMissing: true,
  });
  if (!state || state.type === 'pending') {
    return <ComponentPreviewLoading />;
  }
  if (state.type === 'rejected') {
    return (
      <TextSmall>
        Error loading info ({component.source.slug}): {formatError(state.error)}
      </TextSmall>
    );
  }
  const onReplaceComponent = () => {
    if (section.element.current == null) {
      return;
    }
    session.analytics.track({
      event: 'project:replace_component_button_click',
      component_slug: section.element.current?.source.slug,
    });
    navigation.goto({
      path: 'edit/replace-component',
    });
  };
  return (
    <ComponentPreviewLayout
      name={state.value.name}
      isEdited={sectionEditing.resetElements != null}
      menu={[
        menuItem({
          label: 'Reset all changes',
          onClick: () => {
            sectionEditing.resetElements?.();
            sectionEditing.resetCopy?.();
            analytics.track({
              event: 'blocks/reset',
              object: 'all',
              subject: 'section',
              componentSlugs: [component.source.slug],
            });
          },
          disabled:
            sectionEditing.resetElements == null &&
            sectionEditing.resetCopy == null,
          disabledReason: 'You haven’t changed the elements or copy',
        }),
        menuItem({
          label: 'Reset elements',
          onClick: () => {
            sectionEditing.resetElements!();
            analytics.track({
              event: 'blocks/reset',
              object: 'elements',
              subject: 'section',
              componentSlugs: [component.source.slug],
            });
          },
          disabled: sectionEditing.resetElements == null,
          disabledReason: 'You haven’t changed the elements',
        }),
        menuItem({
          label: 'Reset copy',
          onClick: () => {
            sectionEditing.resetCopy!();
            analytics.track({
              event: 'blocks/reset',
              object: 'copy',
              subject: 'section',
              componentSlugs: [component.source.slug],
            });
          },
          disabled: sectionEditing.resetCopy == null,
          disabledReason: 'You haven’t changed the copy',
        }),
      ].filter(exists)}
      menuEnabled={
        sectionEditing.resetElements != null || sectionEditing.resetCopy != null
      }
      onReplaceComponent={onReplaceComponent}
    />
  );
});

function allElementSlugs(root: ElementInstance): readonly string[] {
  const slugs = new Set<string>();
  function walk(e: ElementInstance) {
    slugs.add(e.source.slug);
    for (const slotOverride of e.slotOverrides.values()) {
      for (const child of slotOverride.items) {
        walk(child);
      }
    }
  }
  walk(root);
  return [...slugs];
}

const ViewOptions = observer(function _ViewOptions({
  element,
  library,
  flags,
}: {
  element: ElementInstance;
  library: LibraryCache;
  flags: FeatureFlagsStore;
}) {
  const options = globalElementViewOptions.get(element);
  const allSlugs = allElementSlugs(element);
  const allPayloads = allSlugs
    .map((slug) => library.getCachedPublicComponentJson(slug)?.value?.payload)
    .filter(exists);

  const rows: React.ReactNode[] = [];

  const hasMenus = allPayloads.some(componentHasMenus);
  const setOpenMenus = React.useCallback(
    (v: boolean) => (options.openMenus = v),
    [options],
  );
  if (hasMenus && !isNavbarWithDropdownsOnly(element.source.slug)) {
    rows.push(
      <LabelledOption label="Menu" key="menu">
        <ButtonPalette>
          <RadioButtonGroup
            options={SHOW_HIDE_OPTIONS}
            value={options.openMenus}
            onChange={setOpenMenus}
          />
        </ButtonPalette>
      </LabelledOption>,
    );
  }

  const numTabs = Math.max(...allPayloads.map(countComponentTabs), 0);
  if (numTabs > 0) {
    const getOptions = () => {
      if (numTabs <= 4) {
        return (
          <ButtonPalette>
            <RadioButtonGroup
              options={Array.from({ length: numTabs }, (_, i) => ({
                label: `${i + 1}`,
                value: i,
              }))}
              value={options.tabIndex}
              onChange={(v) => (options.tabIndex = v)}
            />
          </ButtonPalette>
        );
      }
      const menuOptions = Array.from({ length: numTabs }, (_, i) =>
        menuItem({
          label: `${i + 1}`,
          onClick: () => (options.tabIndex = i),
          checked: options.tabIndex === i,
        }),
      );
      return <SelectButton menu={menuOptions} />;
    };
    rows.push(
      <LabelledOption label="Show Tab" key="tab">
        {getOptions()}
      </LabelledOption>,
    );
  }

  if (rows.length === 0) return null;
  return (
    <Rows spacing={8}>
      <TextSmall>Visibility</TextSmall>
      {rows}
    </Rows>
  );
});

const SHOW_HIDE_OPTIONS = [
  { label: 'Show', value: true },
  { label: 'Hide', value: false },
];
